import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";

import type {
    QuoteImageBlock,
    ThreeByThreeImageBlock as Value,
} from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/components/three_by_three.html` (and the inline `quote_image_block.html`
// markup it composes) verbatim — every classname is load-bearing for the
// existing SCSS (`_landing-page.scss`), so keep them in sync.
//
// The 3x3 layout is asymmetric (NOT six flat articles): the left column renders
// quote_left_1 as a `--half` article followed by quote_left_2 + quote_left_3 as
// `--quarter` articles; the right column renders quote_right_1 + quote_right_2
// as `--quarter` articles followed by quote_right_3 as a `--half` article. Four
// `landing-page-reviews__container` wrappers total.
type IProps = Immutable<{
    value: Value;
}>;

type IQuote = Immutable<QuoteImageBlock>;

// Mirror the legacy `{% round_to_half value.stars|to_float %}` filter, which is
// `round(value * 2) / 2` in Python. Python's `round` uses banker's rounding
// (round-half-to-even), so an exact `x.25` input (a CMS author can type `3.25`
// into the DecimalBlock) rounds the `2x + 0.5` tie DOWN to even `x.0`, not up.
// JS `Math.round` is round-half-up and would render `x.5` instead — diverging
// from legacy. Replicate the round-half-to-even tie-break so the rendered star
// count matches exactly.
const roundToHalf = (value: number): number => {
    const doubled = value * 2;
    const floor = Math.floor(doubled);
    const diff = doubled - floor;
    if (diff < 0.5) {
        return floor / 2;
    }
    if (diff > 0.5) {
        return (floor + 1) / 2;
    }
    return (floor % 2 === 0 ? floor : floor + 1) / 2;
};

// Legacy emitted `style="background-image: url(<url>);"`. When the image is
// null the legacy markup rendered `url()` (an empty/broken url); we instead
// omit the inline style entirely while keeping the wrapper element — matching
// the sibling LifestyleModule precedent. Legacy read `image.file.url`; the
// reactivated serializer exposes the equivalent value as `image.url`.
const backgroundImageStyle = (
    image: IQuote["image"] | undefined,
): React.CSSProperties | undefined => {
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url})` };
};

// The half/quarter articles are identical markup except for the
// `--half`/`--quarter` modifier; one subcomponent parameterized by `modifier`.
const QuoteArticle = (
    props: Immutable<{
        quote: IQuote | null | undefined;
        modifier: "half" | "quarter";
    }>,
): React.JSX.Element => {
    const v = props.quote ?? null;
    const quote = v?.quote;
    // Apply the legacy `{% round_to_half value.stars|to_float %}` + `|default:0`
    // before StarRating. StarRating's thresholds (>= i+0.25 half, >= i+0.75 full)
    // match legacy `star-rating.html` for most values, but diverge at exact
    // `x.25` inputs: round_to_half's banker's rounding sends `x.25` to `x.0`
    // (outline), whereas the raw value crosses the half threshold (half star).
    // The `: 0` fallback mirrors the legacy `|default:0`.
    const rating = v?.stars ? roundToHalf(parseFloat(v.stars)) : 0;
    const hoverActive = quote
        ? " landing-page-reviews__container--hover-active"
        : "";
    return (
        <article
            className={`landing-page-reviews__container--${props.modifier}${hoverActive}`}
        >
            <div
                className="landing-page-reviews__image"
                style={backgroundImageStyle(v?.image)}
            ></div>
            {quote && (
                <div className="landing-page-reviews-hover">
                    <div className="landing-page-reviews-hover__content">
                        <StarRating
                            rating={rating}
                            className="landing-page-reviews-hover__rating"
                        />
                        {/* Curly quotation marks (U+201C / U+201D) are literal
                            in the legacy template — render them verbatim. */}
                        <h3 className="landing-page-reviews-hover__quote">
                            {"“"}
                            {quote}
                            {"”"}
                        </h3>
                        <h4 className="landing-page-reviews-hover__attribution">
                            {v?.attribution}
                        </h4>
                    </div>
                </div>
            )}
        </article>
    );
};

export const ThreeByThreeImageBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    return (
        <>
            <h2 className="landing-page-reviews__headline landing-page__title">
                {v.review_title}
            </h2>
            <div className="landing-page-reviews__container-left">
                <div className="landing-page-reviews__container">
                    <QuoteArticle quote={v.quote_left_1} modifier="half" />
                </div>
                <div className="landing-page-reviews__container">
                    <QuoteArticle quote={v.quote_left_2} modifier="quarter" />
                    <QuoteArticle quote={v.quote_left_3} modifier="quarter" />
                </div>
            </div>
            <div className="landing-page-reviews__container-right">
                <div className="landing-page-reviews__container">
                    <QuoteArticle quote={v.quote_right_1} modifier="quarter" />
                    <QuoteArticle quote={v.quote_right_2} modifier="quarter" />
                </div>
                <div className="landing-page-reviews__container">
                    <QuoteArticle quote={v.quote_right_3} modifier="half" />
                </div>
            </div>
        </>
    );
};
